import * as THREE from 'three';
import { MeshBuilder } from '@/flora/GeometryBuilder';
import {
  poseHeightMeters,
  poseLengthMeters,
  type FaunaAttachment,
  type FaunaClass,
  type FaunaDefinition,
  type FaunaPose,
} from './FaunaDefinition';

/**
 * Proxy silhouettes for placed fauna — the geometry a record renders as until it has a mesh.
 *
 * These are **massing, not models**, and they are meant to look like it. The same decision
 * `SiteObjectWorld` already makes for a prop whose `.glb` has not downloaded: the object appears
 * immediately at the size the record says, and swaps to authored geometry when one arrives. A fauna
 * catalog authored ahead of its assets (see `docs/FAUNA_SCHEMA.md`) makes that the normal state
 * rather than a transient one, which raises the bar slightly — a placeholder you will look at for
 * months has to be *legible*, not merely present.
 *
 * So the shapes are built to read at the one thing staffage is for: scale. A quadruped has legs and
 * a head because the leg-to-body ratio is what tells you a deer is not a rabbit at fifty metres; a
 * figure is the abstract tapered column architectural drawings have used for a century, because a
 * detailed one would be a specific person and this catalog has no business inventing one.
 *
 * ## Everything is driven by the pose, not the record
 *
 * Dimensions come from {@link poseHeightMeters} / {@link poseLengthMeters}, so a lying dog is built
 * from 0.30 m × 1.10 m and a standing one from 0.58 m × 1.05 m through the same template. That is
 * most of why one quadruped builder is enough for a chipmunk and an elk: the proportions do the
 * work, and the record already carries them honestly.
 */

/** The two material slots a fauna view has, mirroring the record's `colors.body` / `colors.accent`. */
export interface FaunaGeometryParts {
  body: THREE.BufferGeometry | null;
  accent: THREE.BufferGeometry | null;
}

/** Which template a class is built from. Derived, not authored — see the note in `FaunaDefinition`. */
export type FaunaForm = 'quadruped' | 'bird' | 'insect' | 'squat' | 'serpentine' | 'figure';

const FORM_BY_CLASS: Record<FaunaClass, FaunaForm> = {
  mammal: 'quadruped',
  bird: 'bird',
  insect: 'insect',
  amphibian: 'squat',
  reptile: 'serpentine',
  person: 'figure',
};

export function faunaForm(definition: FaunaDefinition): FaunaForm {
  return FORM_BY_CLASS[definition.class];
}

/** Segment counts. Low on purpose: a silhouette gains nothing from a smooth sphere. */
const SEG = { lat: 8, lon: 10 } as const;

function ellipsoid(rx: number, ry: number, rz: number, x: number, y: number, z: number): {
  geometry: THREE.BufferGeometry;
  matrix: THREE.Matrix4;
} {
  return {
    geometry: new THREE.SphereGeometry(1, SEG.lon, SEG.lat),
    matrix: new THREE.Matrix4().compose(
      new THREE.Vector3(x, y, z),
      new THREE.Quaternion(),
      new THREE.Vector3(rx, ry, rz),
    ),
  };
}

function box(
  width: number,
  height: number,
  depth: number,
  x: number,
  y: number,
  z: number,
): { geometry: THREE.BufferGeometry; matrix: THREE.Matrix4 } {
  return {
    geometry: new THREE.BoxGeometry(width, height, depth),
    matrix: new THREE.Matrix4().makeTranslation(x, y, z),
  };
}

/** A flat wing/fin quad in the XZ plane, offset to one side. */
function wing(span: number, chord: number, x: number, y: number, z: number, tilt: number): {
  geometry: THREE.BufferGeometry;
  matrix: THREE.Matrix4;
} {
  const geometry = new THREE.PlaneGeometry(span, chord);
  const matrix = new THREE.Matrix4()
    .makeTranslation(x, y, z)
    .multiply(new THREE.Matrix4().makeRotationX(-Math.PI / 2))
    .multiply(new THREE.Matrix4().makeRotationY(tilt));
  return { geometry, matrix };
}

interface Dims {
  length: number;
  height: number;
  width: number;
  wingspan: number;
  /** True when the pose is off the ground — legs are tucked and wings are out. */
  airborne: boolean;
}

/**
 * Build the silhouette for a definition in a given pose.
 *
 * Pure and deterministic: no seeding, no randomness. Two rabbits at the same maturity differ by
 * their instance's `sizeVariation` scale and nothing else, which is what keeps a flock cheap — every
 * goldfinch in it can share one geometry if a caller wants to, and none of them wobbles on rebuild.
 */
export function buildFaunaGeometry(
  definition: FaunaDefinition,
  pose: FaunaPose,
  options: { attachment?: FaunaAttachment } = {},
): FaunaGeometryParts {
  const attachment = options.attachment ?? pose.attachment ?? definition.attachment[0] ?? 'ground';
  const dims: Dims = {
    length: poseLengthMeters(definition, pose),
    height: poseHeightMeters(definition, pose),
    width: definition.size.widthMeters,
    wingspan: definition.size.wingspanMeters ?? 0,
    airborne: attachment === 'air',
  };

  const body = new MeshBuilder();
  const accent = new MeshBuilder();

  switch (faunaForm(definition)) {
    case 'quadruped':
      buildQuadruped(body, accent, dims);
      break;
    case 'bird':
      buildBird(body, accent, dims);
      break;
    case 'insect':
      buildInsect(body, accent, dims);
      break;
    case 'squat':
      buildSquat(body, accent, dims);
      break;
    case 'serpentine':
      buildSerpentine(body, accent, dims);
      break;
    case 'figure':
      buildFigure(body, accent, dims);
      break;
  }

  return { body: body.build(), accent: accent.build() };
}

/* --------------------------------- Templates ---------------------------------- */

/**
 * Four legs, a barrel, a neck and a head.
 *
 * The leg fraction is the one number that matters. At 0.42 of total height it produces a deer that
 * reads as leggy and a rabbit that reads as crouched, from the same code, because the records give
 * the rabbit a height barely twice its body depth. A pose that lowers the height — grazing, bedded,
 * lying — shortens the legs with it, which is what those postures actually do.
 */
function buildQuadruped(body: MeshBuilder, accent: MeshBuilder, d: Dims): void {
  const legTop = d.height * 0.42;
  const barrelRy = d.height * 0.22;
  const barrelY = legTop + barrelRy;
  const barrelRz = d.length * 0.34;
  const barrelRx = d.width * 0.5;

  body.add(...unpack(ellipsoid(barrelRx, barrelRy, barrelRz, 0, barrelY, -d.length * 0.04)));

  // Legs. Square section rather than cylinders: at these sizes the extra segments are invisible and
  // the silhouette is identical.
  const legSide = Math.max(d.width * 0.16, 0.008);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      body.add(
        ...unpack(box(legSide, legTop, legSide, sx * d.width * 0.3, legTop / 2, sz * d.length * 0.24)),
      );
    }
  }

  const headR = Math.min(d.height * 0.14, d.width * 0.6);
  const headY = d.height - headR;
  const headZ = d.length * 0.4;
  accent.add(...unpack(ellipsoid(headR, headR, headR * 1.25, 0, headY, headZ)));

  // Neck, as a short taper from the barrel's shoulder to the head.
  const neck = new THREE.CylinderGeometry(headR * 0.7, barrelRx * 0.55, Math.max(headY - barrelY, 0.001), 6);
  const neckMatrix = new THREE.Matrix4().compose(
    new THREE.Vector3(0, (barrelY + headY) / 2, (barrelRz * 0.6 + headZ) / 2),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.5, 0, 0)),
    new THREE.Vector3(1, 1, 1),
  );
  accent.add(neck, { matrix: neckMatrix });

  // Tail: a stub, but the thing that resolves which end is which at a distance.
  accent.add(
    ...unpack(
      ellipsoid(
        headR * 0.42,
        headR * 0.42,
        d.length * 0.07,
        0,
        barrelY + barrelRy * 0.4,
        -barrelRz - d.length * 0.04,
      ),
    ),
  );
}

/**
 * A bird: ovoid body, small head, wedge tail, and either legs or wings.
 *
 * The legs/wings switch is driven by attachment rather than by pose id, so a record that adds a
 * `gliding` pose gets the right silhouette without this file learning the word.
 */
function buildBird(body: MeshBuilder, accent: MeshBuilder, d: Dims): void {
  const legTop = d.airborne ? 0 : d.height * 0.3;
  const bodyRy = d.height * 0.3;
  const bodyY = legTop + bodyRy;

  body.add(...unpack(ellipsoid(d.width * 0.5, bodyRy, d.length * 0.4, 0, bodyY, 0)));

  if (!d.airborne) {
    const legSide = Math.max(d.width * 0.12, 0.004);
    for (const sx of [-1, 1]) {
      body.add(...unpack(box(legSide, legTop, legSide, sx * d.width * 0.22, legTop / 2, -d.length * 0.02)));
    }
  }

  const headR = d.height * 0.17;
  accent.add(...unpack(ellipsoid(headR, headR, headR, 0, d.height - headR, d.length * 0.26)));

  // Beak — tiny, and the reason a 90 mm hummingbird still reads as a bird rather than a pebble.
  const beak = new THREE.ConeGeometry(headR * 0.35, d.length * 0.16, 5);
  accent.add(beak, {
    matrix: new THREE.Matrix4().compose(
      new THREE.Vector3(0, d.height - headR, d.length * 0.26 + headR + d.length * 0.06),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0)),
      new THREE.Vector3(1, 1, 1),
    ),
  });

  // Tail wedge.
  accent.add(...unpack(box(d.width * 0.5, d.height * 0.07, d.length * 0.3, 0, bodyY, -d.length * 0.46)));

  if (d.airborne && d.wingspan > 0) {
    const half = (d.wingspan - d.width) / 2;
    for (const sx of [-1, 1]) {
      accent.add(
        ...unpack(
          wing(half, d.length * 0.42, sx * (d.width * 0.5 + half / 2), bodyY, 0, sx * 0.25),
        ),
      );
    }
  }
}

/** A body, a thorax bump, and wings whenever the record has a wingspan to spread. */
function buildInsect(body: MeshBuilder, accent: MeshBuilder, d: Dims): void {
  const bodyRy = d.height * 0.42;
  const bodyY = d.airborne ? d.height * 0.5 : bodyRy;

  body.add(...unpack(ellipsoid(d.width * 0.5, bodyRy, d.length * 0.42, 0, bodyY, -d.length * 0.08)));
  const headR = d.height * 0.36;
  accent.add(...unpack(ellipsoid(headR, headR, headR, 0, bodyY, d.length * 0.36)));

  if (d.wingspan > 0) {
    const half = (d.wingspan - d.width) / 2;
    // Open flat when perched with wings displayed, angled up when flying — a butterfly at rest and
    // one in the air are the two silhouettes anyone can tell apart.
    const tilt = d.airborne ? 0.55 : 0.08;
    for (const sx of [-1, 1]) {
      accent.add(
        ...unpack(
          wing(half, d.length * 0.9, sx * (d.width * 0.5 + half / 2), bodyY + bodyRy * 0.5, 0, sx * tilt),
        ),
      );
    }
  }
}

/** Frogs and toads: mostly body, folded hind legs, eyes proud of the head. */
function buildSquat(body: MeshBuilder, accent: MeshBuilder, d: Dims): void {
  const bodyRy = d.height * 0.5;
  body.add(...unpack(ellipsoid(d.width * 0.5, bodyRy, d.length * 0.5, 0, bodyRy, 0)));
  for (const sx of [-1, 1]) {
    body.add(
      ...unpack(ellipsoid(d.width * 0.18, bodyRy * 0.5, d.length * 0.28, sx * d.width * 0.42, bodyRy * 0.5, -d.length * 0.1)),
    );
  }
  const eyeR = d.height * 0.18;
  for (const sx of [-1, 1]) {
    accent.add(...unpack(ellipsoid(eyeR, eyeR, eyeR, sx * d.width * 0.22, d.height - eyeR * 0.6, d.length * 0.24)));
  }
}

/** Lizards and snakes: a low tapered body, no legs worth drawing at this size. */
function buildSerpentine(body: MeshBuilder, accent: MeshBuilder, d: Dims): void {
  const bodyRy = d.height * 0.5;
  body.add(...unpack(ellipsoid(d.width * 0.5, bodyRy, d.length * 0.42, 0, bodyRy, -d.length * 0.06)));
  const headR = Math.min(d.height * 0.5, d.width * 0.5);
  accent.add(...unpack(ellipsoid(headR, headR * 0.8, headR * 1.4, 0, bodyRy, d.length * 0.4)));
}

/**
 * The architectural scale figure: two legs, a tapered torso, a head. No face, no hands, no clothes.
 *
 * Abstract on purpose. A figure exists to say how tall the fence is, and the moment it acquires
 * detail it starts making claims — about who uses this garden, what they look like — that a catalog
 * shipped with an application has no standing to make. Every drawing convention worth copying here
 * settled on the same answer a century ago.
 */
function buildFigure(body: MeshBuilder, accent: MeshBuilder, d: Dims): void {
  const legTop = d.height * 0.47;
  const shoulderY = d.height * 0.82;
  const headR = d.height * 0.055;

  const legWidth = d.width * 0.17;
  for (const sx of [-1, 1]) {
    body.add(...unpack(box(legWidth, legTop, Math.max(d.length * 0.6, legWidth), sx * d.width * 0.14, legTop / 2, 0)));
  }

  // Torso as a taper — narrow at the waist, shoulder-width at the top.
  const torso = new THREE.CylinderGeometry(d.width * 0.5, d.width * 0.3, shoulderY - legTop, 8);
  body.add(torso, {
    matrix: new THREE.Matrix4().compose(
      new THREE.Vector3(0, (legTop + shoulderY) / 2, 0),
      new THREE.Quaternion(),
      // Flattened front-to-back: a person is not a cylinder seen from the side.
      new THREE.Vector3(1, 1, Math.max(d.length / Math.max(d.width, 0.001), 0.35)),
    ),
  });

  // Neck and head in the accent slot, so a figure reads as two tones like everything else.
  accent.add(...unpack(box(d.width * 0.16, d.height - shoulderY - headR * 1.4, d.width * 0.16, 0, (shoulderY + d.height - headR * 2) / 2, 0)));
  accent.add(...unpack(ellipsoid(headR, headR * 1.2, headR, 0, d.height - headR * 1.2, 0)));
}

/** Adapter so a template can spread a `{ geometry, matrix }` straight into `MeshBuilder.add`. */
function unpack(made: { geometry: THREE.BufferGeometry; matrix: THREE.Matrix4 }): [
  THREE.BufferGeometry,
  { matrix: THREE.Matrix4 },
] {
  return [made.geometry, { matrix: made.matrix }];
}
